import React, { useCallback } from 'react';
import { cloneDeep, isEqual, uniqWith } from 'lodash';
import isUuid from '../../../../../utils/isUUID';
import { notifications } from '@mantine/notifications';
import {
  getDataTypeOfSelectedItemInNonLookupTable,
  getSingleCellForEachRow,
  isItemDateLike,
  schemaTypesIncludeJsType
} from './helpers';
import { variableTypes } from '../../../../../consts/variableTypes';

const useStructuredUnstructuredSelection = ({
  funds,
  onChangeFunds,
  isIndividualFund,
  isLookup,
  activeFundChildValue,
  fundMappings,
  setPickedFund,
  setSelectedFund,
  setPickedChildFund,
  selectedColumn,
  setSelectedColumn,
  popupErrorMessage
}) => {
  const onSelectionStructuredCells = useCallback(
    (items, cellType) => {
      function processLookup() {
        let _funds = cloneDeep(funds);

        _funds = _funds.map(_fund => {
          if (_fund.id === activeFundChildValue?.foundChildId) {
            _fund.columns = _fund.columns.map(col => {
              if (col.id === activeFundChildValue?.id) {
                return {
                  ...col,
                  values: [...(items || [])]
                  // values: uniqWith([...col.values, ...items], isEqual),
                };
              }

              return col;
            });
          }

          return _fund;
        });

        onChangeFunds(_funds);
      }

      function processNonLookup() {
        const _isUuid = isUuid(selectedColumn?.id);
        let _funds = cloneDeep(funds);
        let foundValuesAtLeastInOneFund = false; // true => to skip error "Please map Fund name first, then select the other fields" if it has at least one fund with values

        funds.flatMap(fund =>
          fund.tables.flatMap(table =>
            table.columns.flatMap(column => {
              if (column.id === 'fund name' && column.values.length > 0) {
                foundValuesAtLeastInOneFund = true;
              }
            })
          )
        );

        // check custom fields , they have uuid in id instead lowercased name
        const _id = _isUuid ? selectedColumn?.id : selectedColumn?.name?.toLowerCase();
        const customFieldLowercasedName = selectedColumn?.name?.toLowerCase();

        const hasOneMappedFund =
          (fundMappings.not_mapped_source_funds.length === 0 && funds.length === 1) ||
          fundMappings?.fund_mappings?.length === 1;

        const hasOneMappedFundOrIndividualFund = hasOneMappedFund || isIndividualFund;
        const columnType = selectedColumn ?? (cellType ? [cellType] : null);

        if (columnType) {
          const _columnTypes = selectedColumn?.type || cellType;

          for (let i = 0; i < _funds.length; i++) {
            let _fund = _funds[i];

            const values = items.filter(value => value.text === _fund.source_fund_name);

            for (let j = 0; j < _funds[i].tables.length; j++) {
              let _table = _funds[i].tables[j];

              for (let c = 0; c < _table.columns.length; c++) {
                let _column = _table.columns[c];

                if (hasOneMappedFundOrIndividualFund) {
                  if (_column.id === _id || _column.id === customFieldLowercasedName) {
                    const _filteredItems = items.filter(el => {
                      const _type = typeof el.text;
                      const isDateTime =
                        _columnTypes.includes(variableTypes.DATE_TIME) ||
                        selectedColumn?.format === variableTypes.DATE_TIME_DASH;

                      if (isDateTime && _type === variableTypes.STRING && isItemDateLike(el)) {
                        return true;
                      } else if (
                        isDateTime &&
                        _type === variableTypes.STRING &&
                        !isItemDateLike(el)
                      ) {
                        return false;
                      }

                      return schemaTypesIncludeJsType(_columnTypes, _type);
                    });

                    const hasInvalidTypeValue = items.find(el => {
                      const _type = typeof el.text;
                      const isDateTime =
                        _columnTypes.includes(variableTypes.DATE_TIME) ||
                        selectedColumn?.format === variableTypes.DATE_TIME_DASH;

                      if (isDateTime && _type === variableTypes.STRING && isItemDateLike(el)) {
                        return false;
                      } else if (
                        isDateTime &&
                        _type === variableTypes.STRING &&
                        !isItemDateLike(el)
                      ) {
                        return true;
                      }

                      return !schemaTypesIncludeJsType(_columnTypes, _type);
                    });

                    if (hasInvalidTypeValue)
                      notifications.show({
                        title: 'Invalid value types skipped',
                        color: 'orange',
                        withBorder: true,
                        position: 'bottom-center',
                        autoClose: 3000
                      });

                    if (_filteredItems?.length > 0) _column.values = _filteredItems;

                    break;
                  }
                } else {
                  // For multiple funds first we need to perform "Match Sheet funds" inside the modal
                  if (
                    _column.id === 'fund name' &&
                    _id !== 'fund name' &&
                    _column.values.length === 0 &&
                    !foundValuesAtLeastInOneFund
                  ) {
                    notifications.show({
                      title: 'Please map Fund name first, then select the other fields',
                      color: 'red',
                      withBorder: true,
                      position: 'bottom-center',
                      autoClose: 3000
                    });

                    return;
                  }
                  // Next step is to extract "Fund name" column, only after that we can start extracting other columns
                  if (_column.id === 'fund name' && _column.id === _id) {
                    _column.values = values.map(val => ({
                      ...val,
                      rowId: val.rowId,
                      text: _fund.name
                    }));

                    break;
                  }

                  if ((_column.uuid || _column.id) === _id) {
                    const validItemSelections = items.filter(item => {
                      // As of today dataType could be 'null', 'string', 'number'
                      const dataType = getDataTypeOfSelectedItemInNonLookupTable(item);
                      const isDateTime =
                        _columnTypes.includes(variableTypes.DATE_TIME) ||
                        selectedColumn?.format === variableTypes.DATE_TIME_DASH;

                      if (dataType === variableTypes.STRING && isItemDateLike(item) && isDateTime) {
                        return true;
                      } else if (
                        dataType === variableTypes.STRING &&
                        !isItemDateLike(item) &&
                        isDateTime
                      ) {
                        return false;
                      }

                      return schemaTypesIncludeJsType(_columnTypes, dataType);
                    });

                    const _fundColumn = _table.columns.find(val => val.id === 'fund name');

                    const _rowsId = _fundColumn.values.map(r => r.rowId);

                    const result = validItemSelections.filter(val => _rowsId.includes(val.rowId));

                    if (result?.length > 0) _column.values = result;
                    else {
                      notifications.show({
                        title: `No new valid values to select ${_fund.name ? 'for fund: ' + _fund.name : ''}`,
                        color: 'red',
                        withBorder: true,
                        position: 'bottom-center',
                        autoClose: 3000
                      });
                    }
                    break;
                  }
                }
              }
            }
          }

          onChangeFunds(_funds);
          setPickedFund(funds.map(item => item.id));
          setSelectedFund(funds.map(item => item.id));
          setPickedChildFund(
            funds
              .map(item => item.tables)
              .flat()
              .map(item => item.id)
          );
          setSelectedColumn(null);
          foundValuesAtLeastInOneFund = false;
        }
      }

      if (isLookup) {
        processLookup();
      } else {
        processNonLookup();
      }
    },
    [
      fundMappings,
      isIndividualFund,
      funds,
      isLookup,
      activeFundChildValue,
      selectedColumn,
      popupErrorMessage
    ]
  );

  const onSelectionUnStructuredCells = useCallback(
    items => {
      if (!activeFundChildValue) return;

      const _funds = cloneDeep(funds);

      // Handle Lookup Case
      const processLookup = _table => {
        const targetColumn = _table.columns.find(col => col.id === activeFundChildValue.id);

        if (targetColumn) {
          const updatedTargetColumnValues = getSingleCellForEachRow(targetColumn.values, items);
          targetColumn.values = uniqWith(updatedTargetColumnValues, isEqual);
          onChangeFunds(_funds);
        }
      };

      const processNonLookup = () => {
        // filters out only type matching selections
        const validItemSelections = items.filter(item => {
          // As of today dataType could be 'null', 'string', 'number'
          const dataType = getDataTypeOfSelectedItemInNonLookupTable(item);
          if (
            dataType === 'string' &&
            isItemDateLike(item) &&
            (activeFundChildValue?.type?.includes('datetime') ||
              activeFundChildValue?.format === variableTypes.DATE_TIME_DASH)
          ) {
            return true;
          } else if (
            dataType === 'string' &&
            !isItemDateLike(item) &&
            (activeFundChildValue?.type?.includes('datetime') ||
              activeFundChildValue?.format === variableTypes.DATE_TIME_DASH)
          ) {
            return false;
          } else if (
            isItemDateLike(item) &&
            !(
              activeFundChildValue?.type?.includes('datetime') ||
              activeFundChildValue?.format === variableTypes.DATE_TIME_DASH
            )
          ) {
            return false;
          }

          return schemaTypesIncludeJsType(activeFundChildValue?.type, dataType);
        });
        // used to show the notification for invalid selections
        const hasInvalidDataType = items.some(item => {
          const dataType = getDataTypeOfSelectedItemInNonLookupTable(item);
          if (
            dataType === 'string' &&
            isItemDateLike(item) &&
            (activeFundChildValue?.type?.includes('datetime') ||
              activeFundChildValue?.format === variableTypes.DATE_TIME_DASH)
          ) {
            return false;
          } else if (
            dataType === 'string' &&
            !isItemDateLike(item) &&
            (activeFundChildValue?.type?.includes('datetime') ||
              activeFundChildValue?.format === variableTypes.DATE_TIME_DASH)
          ) {
            return true;
          } else if (
            isItemDateLike(item) &&
            !(
              activeFundChildValue?.type?.includes('datetime') ||
              activeFundChildValue?.format === variableTypes.DATE_TIME_DASH
            )
          ) {
            return true;
          }

          return !schemaTypesIncludeJsType(activeFundChildValue?.type, dataType);
        });

        if (hasInvalidDataType) {
          notifications.show({
            title: 'Type Error',
            message:
              'Unstructured data type is incorrect. Please select values with correct data type.',
            color: 'red',
            withBorder: true,
            position: 'bottom-center',
            autoClose: 2000
          });
        }

        for (let i = 0; i < _funds.length; i++) {
          const fund = _funds[i];
          const matchingTable = fund.tables.find(
            table => table.id === activeFundChildValue.foundChildId
          );

          if (matchingTable) {
            const targetColumn = matchingTable.columns.find(
              col => col.id === activeFundChildValue.id
            );

            if (targetColumn) {
              const updatedTargetColumnValues = getSingleCellForEachRow(
                targetColumn.values,
                validItemSelections
              );
              targetColumn.values = uniqWith(updatedTargetColumnValues, isEqual);
              onChangeFunds(_funds);
            }
          }
        }
      };

      // Execute based on lookup condition
      if (isLookup) {
        const matchingTable = _funds.find(table => table.id === activeFundChildValue.foundChildId);
        if (matchingTable) processLookup(matchingTable);
      } else {
        processNonLookup();
      }
    },
    [activeFundChildValue, isLookup, funds]
  );

  return {
    onSelectionStructuredCells,
    onSelectionUnStructuredCells
  };
};

export default useStructuredUnstructuredSelection;
